import { useState } from 'react';

import { Button, Input } from '@/components/ui';
import { useClientsSearch, type Client } from '@/lib/clients-store';

import { cidadeDoCliente, fraseDaConfirmacao, nomeDoCliente } from './vinculos-texto';

/**
 * #571 (14/10/2026) — a escolha do cliente daqui para um cadastro de fora (Auvo ou Omie), na tela de Vínculos.
 *
 * ⚠️ Antes, o "Vincular à mão" pedia o **Id do cliente do IGM**: um código interno que ninguém sabe de cabeça. O
 * Matheus vinculou o Le Ville copiando o código da ficha. Agora: busca por **nome fantasia, razão social ou CNPJ**,
 * cada resultado mostra o fantasia, a razão social e a cidade, e **nada é gravado sem a pergunta** *"Vincular le ville
 * (Auvo) a CONDOMINIO LE VILLE?"*. ❌ O Id interno não aparece nem é pedido.
 *
 * ✅ Não é motor novo: a busca é a de `/api/v1/clients` (`useClientsSearch`), a mesma do seletor da Manutenção.
 */
export function EscolherClienteDoVinculo({
  nomeDeFora,
  sistema,
  onConfirmar,
  onCancelar,
}: {
  nomeDeFora: string | null;
  sistema: 'auvo' | 'omie';
  onConfirmar: (clienteId: string) => void;
  onCancelar: () => void;
}) {
  const [termo, setTermo] = useState('');
  const [escolhido, setEscolhido] = useState<Client | null>(null);
  const buscando = termo.trim().length >= 2;
  const busca = useClientsSearch({ q: buscando ? termo : '', limit: 8 });
  const resultados = buscando ? (busca.data ?? []) : [];

  if (escolhido) {
    return (
      <div className="w-full space-y-2" data-testid="vinculo-confirmacao">
        <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          {fraseDaConfirmacao(nomeDeFora, sistema, nomeDoCliente(escolhido))}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => onConfirmar(escolhido.id)} data-testid="vinculo-confirmar">
            Vincular
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEscolhido(null)}>
            Escolher outro
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancelar}>
            Cancelar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="w-full max-w-md"
          autoFocus
          placeholder="Buscar por nome fantasia, razão social ou CNPJ"
          aria-label="Buscar o cliente para vincular"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          data-testid="vinculo-busca"
        />
        <Button size="sm" variant="ghost" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
      {!buscando && (
        <p className="text-xs text-ink-3 dark:text-ink-dark-3">Digite ao menos duas letras ou o CNPJ.</p>
      )}
      {buscando && busca.isLoading && <p className="text-xs text-ink-3 dark:text-ink-dark-3">Buscando…</p>}
      {buscando && !busca.isLoading && resultados.length === 0 && (
        <p className="text-xs text-ink-3 dark:text-ink-dark-3" data-testid="vinculo-busca-vazia">
          Nenhum cliente encontrado para &quot;{termo.trim()}&quot;.
        </p>
      )}
      {resultados.length > 0 && (
        <ul className="space-y-1" data-testid="vinculo-resultados">
          {resultados.map((c) => {
            const cidade = cidadeDoCliente(c);
            const razao = (c.razaoSocial ?? '').trim();
            const nome = nomeDoCliente(c);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => setEscolhido(c)}
                  className="min-h-[44px] w-full rounded-md border border-line px-3 py-2 text-left hover:bg-paper-warm dark:border-line-dark dark:hover:bg-paper-dark-warm"
                  data-testid="vinculo-resultado"
                >
                  <span className="block text-sm font-medium text-ink-1 dark:text-ink-dark-1">{nome}</span>
                  <span className="block text-xs text-ink-3 dark:text-ink-dark-3">
                    {razao && razao !== nome ? razao : 'mesma razão social'}
                    {' · '}
                    {cidade ?? 'cidade não informada'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
